import { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Loader, X } from 'lucide-react'
import { alarmApi } from '../lib/alarmApi'

const OPERATORS = ['Greater Than', 'Less Than', 'Greater Or Equal To', 'Less Or Equal To', 'Equal To', 'Not Equal To']
const OP_SYMBOL = {
  'Greater Than': '>', 'Less Than': '<', 'Greater Or Equal To': '>=',
  'Less Or Equal To': '<=', 'Equal To': '==', 'Not Equal To': '!=',
}
// Logical opposite of each operator — used to auto-fill the Reset conditions as
// the negation of the Trigger conditions (matches production wqdatalive). e.g.
// trigger "> 10" → reset "<= 10".
const OPPOSITE_OP = {
  'Greater Than': 'Less Or Equal To',
  'Less Or Equal To': 'Greater Than',
  'Less Than': 'Greater Or Equal To',
  'Greater Or Equal To': 'Less Than',
  'Equal To': 'Not Equal To',
  'Not Equal To': 'Equal To',
}
// De Morgan: the opposite of "A AND B" is "notA OR notB", so the group operator
// flips too when mirroring trigger → reset.
const OPPOSITE_COND_OP = (op) => (op === '|' ? '&' : '|')
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
// ISO day-of-week numbering used by the backend (Mon=1 … Sun=7)
const DAY_TO_ISO = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6, Sunday: 7 }
// value = IANA name (sent to backend); label = friendly display
const TIMEZONES = [
  { value: 'America/Los_Angeles', label: '(UTC-08:00) America/Los_Angeles' },
  { value: 'America/Denver', label: '(UTC-07:00) America/Denver' },
  { value: 'America/Chicago', label: '(UTC-06:00) America/Chicago' },
  { value: 'America/New_York', label: '(UTC-05:00) America/New_York' },
  { value: 'UTC', label: '(UTC+00:00) UTC' },
  { value: 'Asia/Kolkata', label: '(UTC+05:30) Asia/Kolkata' },
]

const emptyCondition = () => ({ loggerId: '', parameterId: '', operator: 'Greater Than', value: '' })
const rand32 = () => Array.from({ length: 32 }, () => Math.floor(Math.random() * 36).toString(36)).join('')

// ── Reverse mappings, used to repopulate the form when editing ──
// tree symbol ("<", ">=" …) → human operator label
const SYMBOL_TO_OP = Object.fromEntries(Object.entries(OP_SYMBOL).map(([label, sym]) => [sym, label]))
const REL_OPS = new Set(['<', '<=', '>', '>=', '==', '!='])
const COND_OPS = new Set(['&', '|'])
// ISO day number (Mon=1 … Sun=7) → day name
const ISO_TO_DAY = { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday' }

// seconds-since-midnight → "HH:MM" (or '' when unset)
const secondsToHms = (sec) => {
  if (sec === null || sec === undefined || sec === '') return ''
  const n = Number(sec)
  if (Number.isNaN(n)) return ''
  const h = Math.floor(n / 3600)
  const m = Math.floor((n % 3600) / 60)
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

// Parse a Lisp-style condition tree — "(>= $p5 10)" or "(& (>= $p1 10) (< $p2 0))" —
// back into a flat list of UI leaves [{ parameterId, operator, value }].
// This is the inverse of trie_op_tree.py (TrieOpTreeConverter) and lets the
// modal show the conditions that were saved with the alarm.
const parseTreeToLeaves = (treeString) => {
  // Always return the {condOp, leaves} shape — the caller destructures it. An
  // empty/invalid tree (e.g. an event saved with no reset conditions) must NOT
  // return a bare [], or `leaves` becomes undefined and editing crashes.
  if (!treeString || typeof treeString !== 'string') return { condOp: '&', leaves: [] }
  // 1) tokenize into parens and atoms
  const tokens = []
  let i = 0
  while (i < treeString.length) {
    const ch = treeString[i]
    if (ch === '(' || ch === ')') { tokens.push(ch); i++ }
    else if (/\s/.test(ch)) { i++ }
    else { let j = i; while (j < treeString.length && !/[\s()]/.test(treeString[j])) j++; tokens.push(treeString.slice(i, j)); i = j }
  }
  // 2) parse into a nested array
  let pos = 0
  const parse = () => {
    if (tokens[pos] !== '(') return tokens[pos++]
    pos++ // consume '('
    const list = []
    while (pos < tokens.length && tokens[pos] !== ')') list.push(parse())
    pos++ // consume ')'
    return list
  }
  const root = tokens.length ? parse() : null
  // The top-level group operator ('&' = AND, '|' = OR), so editing an event
  // restores the AND/OR the user picked. Defaults to '&' for a single leaf.
  const condOp = (Array.isArray(root) && COND_OPS.has(root[0])) ? root[0] : '&'
  // 3) walk: condOp groups recurse, relOp nodes become leaves
  const leaves = []
  const walk = (node) => {
    if (!Array.isArray(node) || node.length === 0) return
    const head = node[0]
    if (COND_OPS.has(head)) { node.slice(1).forEach(walk); return }
    if (REL_OPS.has(head)) {
      const m = typeof node[1] === 'string' ? node[1].match(/^\$p(\d+)$/) : null
      if (m) leaves.push({ parameterId: m[1], operator: SYMBOL_TO_OP[head] || 'Greater Than', value: node[2] != null ? String(node[2]) : '' })
    }
  }
  walk(root)
  return { condOp, leaves }
}

/**
 * Full "Event Monitor Settings" modal — create/edit an alarm (event monitor).
 * Matches the XD: Base Settings (left) + Trigger Conditions / Trigger Action
 * (Email/SMS/Push/Advanced) / Reset Conditions / Reset Action (right).
 */
export function EventMonitorSettingsModal({ open, onClose, onSaved, projectId, sites = [], editingAlarm = null }) {
  // ── Base settings ──
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [timezone, setTimezone] = useState('America/New_York')
  const [restrictRange, setRestrictRange] = useState(false)
  const [activeDays, setActiveDays] = useState({ Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true, Saturday: false, Sunday: false })
  const [activeFrom, setActiveFrom] = useState('')
  const [activeTo, setActiveTo] = useState('')
  const [multiTrigger, setMultiTrigger] = useState(false)
  const [enabled, setEnabled] = useState(true)

  // ── Trigger / reset ──
  const [triggerConditions, setTriggerConditions] = useState([emptyCondition()])
  // How multiple conditions combine: '&' = AND (all must match), '|' = OR (any).
  const [triggerCondOp, setTriggerCondOp] = useState('&')
  const [triggerPeriodMin, setTriggerPeriodMin] = useState(10)
  const [resetConditions, setResetConditions] = useState([{ ...emptyCondition(), operator: 'Less Or Equal To' }])
  const [resetCondOp, setResetCondOp] = useState('&')
  // While false, the Reset conditions auto-track the opposite of the Trigger
  // conditions. The first manual edit to a Reset field flips this true so the
  // user's custom reset is no longer overwritten.
  const resetDirtyRef = useRef(false)
  const [resetPeriodMin, setResetPeriodMin] = useState(10)

  // ── Actions ──
  const [actionTab, setActionTab] = useState('Email')
  const [message, setMessage] = useState('')
  // Each email recipient is either a single address (kind:'email', to) or a
  // recipient group (kind:'group', groupId). See recipientGroups below.
  const [emailRecipients, setEmailRecipients] = useState([]) // [{ kind, to, groupId, notification }]
  const [smsRecipients, setSmsRecipients] = useState([])     // [{ name, phone, notification }]
  // The project's recipient groups (id, name, recipients) for the "Group" option.
  const [recipientGroups, setRecipientGroups] = useState([])
  // The saved trigger action being edited — recipients are reconstructed from it
  // once the recipient groups have also loaded (see effect below).
  const [loadedAction, setLoadedAction] = useState(null)
  // Inline "create a recipient group" form (the standalone /alarms Groups page
  // isn't linked anywhere, so groups are created right here in the picker).
  const [showNewGroup, setShowNewGroup] = useState(false)
  const [newGroupName, setNewGroupName] = useState('')
  const [newGroupRecipients, setNewGroupRecipients] = useState('')
  const [savingGroup, setSavingGroup] = useState(false)
  const [groupError, setGroupError] = useState('')
  const [selectedEmail, setSelectedEmail] = useState(null)   // index of the row the toolbar acts on
  const [selectedSms, setSelectedSms] = useState(null)
  const emailRefs = useRef({})
  const smsRefs = useRef({})
  const [pushReceiveMessage, setPushReceiveMessage] = useState(false)
  const [pushReceiveParam, setPushReceiveParam] = useState(false)
  const [advLog, setAdvLog] = useState(5)
  const [advTransmit, setAdvTransmit] = useState(5)
  const [advTransmitEnabled, setAdvTransmitEnabled] = useState(false)
  const [advSendReport, setAdvSendReport] = useState(false)
  const [advTriggerEWC, setAdvTriggerEWC] = useState(false)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [testing, setTesting] = useState(false)
  const [testMsg, setTestMsg] = useState(null) // { type:'success'|'error', text }

  // Loggers = devices; parameters resolved per device
  const loggers = useMemo(() => {
    const out = []
    sites.forEach(site => (site.devices || []).forEach(dev => {
      const params = (dev.sensors || []).flatMap(s => (s.parameters || []).map(p => ({ id: p.id, name: p.name, unit: p.unit })))
      out.push({ id: dev.id, name: dev.name, siteName: site.name, parameters: params })
    }))
    return out
  }, [sites])
  const paramsForLogger = (loggerId) => loggers.find(l => String(l.id) === String(loggerId))?.parameters || []

  // Reset / hydrate the form when (re)opened
  useEffect(() => {
    if (!open) return
    setError(null); setTestMsg(null); setActionTab('Email')
    setSelectedEmail(null); setSelectedSms(null)

    if (!editingAlarm) {
      // ── create: clean slate ──
      setName(''); setDescription(''); setEnabled(true); setMultiTrigger(false)
      setTimezone('America/New_York')
      setRestrictRange(false)
      setActiveDays({ Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true, Saturday: false, Sunday: false })
      setActiveFrom(''); setActiveTo('')
      setTriggerConditions([emptyCondition()])
      setResetConditions([{ ...emptyCondition(), operator: 'Less Or Equal To' }])
      setTriggerCondOp('&'); setResetCondOp('&')
      resetDirtyRef.current = false   // new event: auto-mirror reset from trigger
      setTriggerPeriodMin(10); setResetPeriodMin(10)
      setEmailRecipients([]); setSmsRecipients([]); setMessage(''); setLoadedAction(null)
      setAdvTransmitEnabled(false); setAdvLog(5); setAdvTransmit(5); setAdvSendReport(false); setAdvTriggerEWC(false)
      return
    }

    // ── edit: hydrate from the saved alarm ──
    setName(editingAlarm.name || '')
    setDescription(editingAlarm.description || '')
    setEnabled(!!editingAlarm.enabled)
    setMultiTrigger(!!editingAlarm.multi_trigger)
    setTimezone(editingAlarm.timezone || 'America/New_York')

    // Active days / time range
    const dayNums = (editingAlarm.active_days || '').split(',').map(s => s.trim()).filter(Boolean).map(Number)
    const dayMap = { Sunday: false, Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false, Saturday: false }
    dayNums.forEach(n => { if (ISO_TO_DAY[n]) dayMap[ISO_TO_DAY[n]] = true })
    const fromHms = secondsToHms(editingAlarm.active_from)
    const toHms = secondsToHms(editingAlarm.active_to)
    const allDays = dayNums.length === 7
    // The alarm was "range restricted" if it is limited to specific days or a time window
    setRestrictRange((dayNums.length > 0 && !allDays) || !!fromHms || !!toHms)
    setActiveDays(allDays || dayNums.length === 0
      ? { Sunday: true, Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true, Saturday: true }
      : dayMap)
    setActiveFrom(fromHms); setActiveTo(toHms)

    // Trigger / reset windows (seconds → minutes)
    setTriggerPeriodMin(Math.round((editingAlarm.alarm_trigger_interval || 0) / 60))
    setResetPeriodMin(Math.round((editingAlarm.alarm_reset_interval || 0) / 60))

    // Conditions: parse the stored trees back into rows, resolving each param's logger
    const resolveLogger = (paramId) => {
      const lg = loggers.find(l => (l.parameters || []).some(p => String(p.id) === String(paramId)))
      return lg ? String(lg.id) : ''
    }
    const toRows = (treeString, fallbackOp) => {
      const { condOp, leaves } = parseTreeToLeaves(treeString)
      const rows = leaves.length === 0
        ? [{ ...emptyCondition(), operator: fallbackOp }]
        : leaves.map(l => ({ loggerId: resolveLogger(l.parameterId), parameterId: l.parameterId, operator: l.operator, value: l.value }))
      return { rows, condOp }
    }
    const trig = toRows(editingAlarm.condition_tree, 'Greater Than')
    const rst = toRows(editingAlarm.reset_tree, 'Less Or Equal To')
    setTriggerConditions(trig.rows); setTriggerCondOp(trig.condOp)
    setResetConditions(rst.rows); setResetCondOp(rst.condOp)
    // Existing event already has a saved reset — treat it as user-defined so we
    // don't clobber it by auto-mirroring from the trigger.
    resetDirtyRef.current = true

    // Recipients + message live in separate Alarmaction rows — fetch them
    setEmailRecipients([]); setSmsRecipients([]); setMessage(''); setLoadedAction(null)
    let cancelled = false
    alarmApi.getAlarmActions(editingAlarm.id)
      .then(res => {
        if (cancelled) return
        const actions = Array.isArray(res.data) ? res.data : (res.data?.results || [])
        const isTransmit = (a) => (a.method || '').toLowerCase() === 'transmit'
        // The email/sms action carries recipients + message.
        const action = actions.find(a => a.type === 'trigger' && !isTransmit(a)) || actions.find(a => !isTransmit(a))
        if (action) {
          setMessage(action.message || '')
          setActionTab((action.method || 'email').toLowerCase() === 'sms' ? 'SMS' : 'Email')
          // Recipients (group vs individual split) are rebuilt in the effect
          // below, which waits until recipientGroups have loaded too.
          setLoadedAction(action)
        }
        // The advanced settings live on a separate method='transmit' action row.
        const adv = actions.find(isTransmit)
        if (adv) {
          try {
            const cfg = JSON.parse(adv.message || '{}')
            setAdvTransmitEnabled(!!cfg.transmitLogEnabled)
            setAdvLog(Math.round((Number(cfg.logInterval) || 0) / 60) || 5)
            setAdvTransmit(Math.round((Number(cfg.transmitInterval) || 0) / 60) || 5)
            setAdvSendReport(!!cfg.sendReport)
            setAdvTriggerEWC(!!cfg.triggerEWC)
          } catch (err) { console.warn('[EventMonitor] bad advanced action JSON', err) }
        }
      })
      .catch(e => console.error('[EventMonitor] failed to load actions', e))
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingAlarm])

  // Keep Reset conditions mirroring the Trigger conditions as their logical
  // opposite (same logger/parameter/value, negated operator, flipped AND/OR) —
  // matches production wqdatalive. Structure-aware so it works even after the
  // user customizes the reset (e.g. a saved event being edited):
  //   • the reset list always matches the trigger list length, so ADDING a
  //     trigger condition appends its opposite to reset (and removing one drops
  //     the matching reset row);
  //   • rows the user has customized are preserved once resetDirtyRef is set —
  //     only brand-new rows (no reset counterpart yet) are auto-filled;
  //   • before any manual reset edit, every row auto-tracks the trigger.
  useEffect(() => {
    setResetConditions(prev => triggerConditions.map((c, i) => {
      if (resetDirtyRef.current && prev[i]) return prev[i]        // keep user's custom row
      return { ...c, operator: OPPOSITE_OP[c.operator] || c.operator }  // mirror new/auto row
    }))
    if (!resetDirtyRef.current) setResetCondOp(OPPOSITE_COND_OP(triggerCondOp))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerConditions, triggerCondOp])

  // Load the project's recipient groups so a recipient can be a Group.
  useEffect(() => {
    if (!open || !projectId) { setRecipientGroups([]); return }
    let cancelled = false
    alarmApi.getRecipientGroups(projectId)
      .then(res => {
        if (cancelled) return
        const list = Array.isArray(res.data) ? res.data : (res.data?.results || [])
        setRecipientGroups(list)
      })
      .catch(e => { if (!cancelled) { console.error('[EventMonitor] failed to load recipient groups', e); setRecipientGroups([]) } })
    return () => { cancelled = true }
  }, [open, projectId])

  // Rebuild recipient rows for an edited event once both the saved action and
  // the recipient groups are available. Linked groups become "Group" rows; the
  // remaining sendto addresses (those not contributed by a selected group)
  // become individual "Email" rows.
  useEffect(() => {
    const action = loadedAction
    if (!action) return
    const sendto = String(action.sendto || '').split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
    const notif = action.include_parameters ? 'Message, Parameter Value' : 'Message'
    if ((action.method || 'email').toLowerCase() === 'sms') {
      setSmsRecipients(sendto.map(phone => ({ name: '', phone, notification: notif })))
      return
    }
    const groupIds = (action.group_ids || []).map(String)
    const groupRows = groupIds.map(gid => ({ kind: 'group', to: '', groupId: gid, notification: notif }))
    const groupEmailSet = new Set()
    groupIds.forEach(gid => {
      const g = recipientGroups.find(x => String(x.id) === gid)
      if (g) String(g.recipients || '').split(/[\n,]+/).map(s => s.trim()).filter(Boolean).forEach(e => groupEmailSet.add(e))
    })
    const individualRows = sendto.filter(e => !groupEmailSet.has(e)).map(to => ({ kind: 'email', to, groupId: '', notification: notif }))
    setEmailRecipients([...groupRows, ...individualRows])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadedAction, recipientGroups])

  if (!open) return null

  // Split a group's stored recipients (newline/comma separated) into addresses.
  const groupEmails = (groupId) => {
    const g = recipientGroups.find(x => String(x.id) === String(groupId))
    return g ? String(g.recipients || '').split(/[\n,]+/).map(s => s.trim()).filter(Boolean) : []
  }

  const updateCond = (list, setList, idx, field, value) => {
    // A manual edit to a Reset field stops the auto-mirror from the trigger.
    if (setList === setResetConditions) resetDirtyRef.current = true
    setList(list.map((c, i) => i === idx ? { ...c, [field]: value } : c))
  }

  // Build the legacy-compatible `conditions` object from the UI rows.
  // One valid rule → a leaf {relOp, values}; many → {condOp, rules:[...]} where
  // condOp is '&' (AND — all match) or '|' (OR — any match), per the group's
  // AND/OR selector. The backend tree converter understands both.
  const buildConditions = (rows, condOp = '&') => {
    const valid = rows.filter(c => c.parameterId && c.value !== '')
    const leaf = (c) => ({ relOp: OP_SYMBOL[c.operator], values: [`$p${c.parameterId}`, Number(c.value)] })
    if (valid.length === 0) return {}
    if (valid.length === 1) return leaf(valid[0])
    return { condOp: (condOp === '|' ? '|' : '&'), rules: valid.map(leaf) }
  }

  const buildAction = () => {
    if (actionTab === 'Email') {
      // A recipient row is either a single address (kind:'email') or a group
      // (kind:'group'); send both to the backend, which resolves groups to
      // their member addresses and records the group link.
      const emails = emailRecipients.filter(r => (r.kind || 'email') === 'email' && r.to).map(r => r.to.trim())
      const groupIds = emailRecipients.filter(r => r.kind === 'group' && r.groupId).map(r => r.groupId)
      if (emails.length === 0 && groupIds.length === 0) return undefined
      return {
        method: 'email',
        message: message || '',
        options: emailRecipients.some(r => r.notification === 'Message, Parameter Value') ? ['params'] : [],
        recipient: { type: groupIds.length && !emails.length ? 'group' : 'individual', emails, groupIds },
      }
    }
    if (actionTab === 'SMS' && smsRecipients.some(r => r.phone)) {
      return {
        method: 'sms',
        message: message || '',
        options: smsRecipients.some(r => r.notification === 'Message, Parameter Value') ? ['params'] : [],
        recipient: { type: 'individual', emails: smsRecipients.filter(r => r.phone).map(r => r.phone) },
      }
    }
    return undefined
  }

  const handleSave = async () => {
    if (!name.trim()) { setError('Event Name is required.'); return }
    setSaving(true); setError(null)
    try {
      // ISO day numbers (Mon=1..Sun=7); when not restricting, send all 7
      const activeDayNums = restrictRange
        ? DAYS.filter(d => activeDays[d]).map(d => DAY_TO_ISO[d]).sort((a, b) => a - b)
        : [1, 2, 3, 4, 5, 6, 7]

      const triggerAction = buildAction()
      // Advanced trigger action (Triggered Transmit/Log Interval, Send Report,
      // Trigger EWC). Intervals are entered in minutes; persist as seconds.
      const advanced = {
        transmitLogEnabled: !!advTransmitEnabled,
        logInterval: advTransmitEnabled ? Math.max(0, Number(advLog) || 0) * 60 : 0,
        transmitInterval: advTransmitEnabled ? Math.max(0, Number(advTransmit) || 0) * 60 : 0,
        sendReport: !!advSendReport,
        triggerEWC: !!advTriggerEWC,
      }
      const payload = {
        project: projectId,
        name: name.trim(),
        description: description || '',
        type: 'alarm',
        enable: !!enabled,
        multiTrigger: !!multiTrigger,
        timezone,
        activeDays: activeDayNums,
        activeFrom: restrictRange ? (activeFrom || '') : '',
        activeTo: restrictRange ? (activeTo || '') : '',
        trigger: {
          alarmInterval: Math.max(0, Number(triggerPeriodMin) || 0) * 60,
          conditions: buildConditions(triggerConditions, triggerCondOp),
          advanced,
          ...(triggerAction ? { action: triggerAction } : {}),
        },
        reset: {
          alarmInterval: Math.max(0, Number(resetPeriodMin) || 0) * 60,
          conditions: buildConditions(resetConditions, resetCondOp),
        },
      }

      if (editingAlarm?.id) {
        await alarmApi.updateEventMonitor(editingAlarm.id, payload)
      } else {
        await alarmApi.createEventMonitor(payload)
      }

      onSaved?.()
      onClose()
    } catch (e) {
      console.error('[EventMonitor] save failed', e)
      const msg = e.response?.data?.message || (e.response?.data ? JSON.stringify(e.response.data).slice(0, 300) : (e.message || 'Save failed'))
      setError(msg)
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTestMsg(null)
    // Expand group rows to their member addresses so TEST reaches them too.
    const recipients = actionTab === 'Email'
      ? emailRecipients.flatMap(r => (r.kind === 'group' ? groupEmails(r.groupId) : (r.to ? [r.to.trim()] : [])))
      : actionTab === 'SMS'
        ? smsRecipients.filter(r => r.phone).map(r => r.phone)
        : []
    if (recipients.length === 0) {
      setTestMsg({ type: 'error', text: 'Add at least one recipient before testing.' })
      return
    }
    setTesting(true)
    try {
      const res = await alarmApi.testEventMonitor({
        method: actionTab === 'SMS' ? 'sms' : 'email',
        recipients,
        message: message || '',
      })
      setTestMsg({ type: 'success', text: res.data?.message || 'Test sent.' })
    } catch (e) {
      setTestMsg({ type: 'error', text: e.response?.data?.message || 'Test failed.' })
    } finally {
      setTesting(false)
    }
  }

  const removeCond = (list, setList, idx) => {
    if (setList === setResetConditions) resetDirtyRef.current = true
    setList(list.filter((_, i) => i !== idx))
  }

  // ── recipient toolbar (ADD / EDIT / REMOVE) — acts on the selected row,
  //    falling back to the last row, so they work on already-added recipients ──
  const addEmail = () => {
    setEmailRecipients(prev => { const next = [...prev, { kind: 'email', to: '', groupId: '', notification: 'Message' }]; setSelectedEmail(next.length - 1); return next })
  }
  // Create a new recipient group inline and select it on a Group row.
  const createGroup = async () => {
    if (!newGroupName.trim()) { setGroupError('Group name is required.'); return }
    const recips = newGroupRecipients.split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
    if (recips.length === 0) { setGroupError('Add at least one recipient email.'); return }
    if (!projectId) { setGroupError('No project selected.'); return }
    setSavingGroup(true); setGroupError('')
    try {
      const res = await alarmApi.createRecipientGroup({ project: projectId, name: newGroupName.trim(), recipients: recips.join('\n') })
      const created = res.data
      // Refresh the groups list so the dropdown includes the new one.
      try {
        const r = await alarmApi.getRecipientGroups(projectId)
        setRecipientGroups(Array.isArray(r.data) ? r.data : (r.data?.results || []))
      } catch { /* keep existing list */ }
      if (created?.id) {
        const gid = String(created.id)
        setEmailRecipients(prev => {
          // Fill the selected/last Group row that has no group yet, else append one.
          const idx = prev.findIndex(x => x.kind === 'group' && !x.groupId)
          if (idx >= 0) return prev.map((x, j) => j === idx ? { ...x, groupId: gid } : x)
          const next = [...prev, { kind: 'group', to: '', groupId: gid, notification: 'Message' }]
          setSelectedEmail(next.length - 1)
          return next
        })
      }
      setShowNewGroup(false); setNewGroupName(''); setNewGroupRecipients('')
    } catch (e) {
      setGroupError(e?.response?.data?.detail || e?.response?.data?.name?.[0] || e?.message || 'Failed to create group.')
    } finally {
      setSavingGroup(false)
    }
  }
  const editEmail = () => {
    const idx = selectedEmail != null ? selectedEmail : emailRecipients.length - 1
    if (idx < 0) return
    setSelectedEmail(idx)
    setTimeout(() => emailRefs.current[idx]?.focus(), 0)
  }
  const removeEmail = () => {
    const idx = selectedEmail != null ? selectedEmail : emailRecipients.length - 1
    if (idx < 0) return
    setEmailRecipients(prev => prev.filter((_, j) => j !== idx))
    setSelectedEmail(null)
  }
  const addSms = () => {
    setSmsRecipients(prev => { const next = [...prev, { name: '', phone: '', notification: 'Message' }]; setSelectedSms(next.length - 1); return next })
  }
  const editSms = () => {
    const idx = selectedSms != null ? selectedSms : smsRecipients.length - 1
    if (idx < 0) return
    setSelectedSms(idx)
    setTimeout(() => smsRefs.current[idx]?.focus(), 0)
  }
  const removeSms = () => {
    const idx = selectedSms != null ? selectedSms : smsRecipients.length - 1
    if (idx < 0) return
    setSmsRecipients(prev => prev.filter((_, j) => j !== idx))
    setSelectedSms(null)
  }

  // ── condition rows renderer (plain function — NOT a nested component, so
  //    inputs keep focus across re-renders). Shared by trigger + reset. ──
  const renderConditionRows = (list, setList, defaultOp, condOp, setCondOp) => (
    <>
      {list.map((c, idx) => (
        <div key={idx}>
          {/* AND/OR connector between conditions. It's one operator for the
              whole group (matching the backend's {condOp, rules} shape), so the
              selector is shown on every gap and changing any updates the group. */}
          {idx > 0 && (
            <div className="flex items-center gap-2 my-1.5">
              <select
                value={condOp}
                onChange={e => { if (setCondOp === setResetCondOp) resetDirtyRef.current = true; setCondOp(e.target.value) }}
                className="bg-white border border-gray-300 rounded px-2 py-1 text-xs font-bold text-[#358ECD] focus:outline-none focus:border-[#358ECD]"
                title="How conditions combine"
              >
                <option value="&">AND</option>
                <option value="|">OR</option>
              </select>
              <span className="flex-1 border-t border-dashed border-gray-200" />
            </div>
          )}
          <div className="flex items-start gap-2 mb-2">
          <div className="grid grid-cols-2 gap-2 flex-1">
            <select value={c.loggerId} onChange={e => updateCond(list, setList, idx, 'loggerId', e.target.value)}
              className="bg-white border common-size15 border-gray-300 rounded px-2 py-2 text-xs text-gray-600 focus:outline-none focus:border-[#358ECD]">
              <option value="">Please Choose A Logger</option>
              {loggers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
            <select value={c.parameterId} onChange={e => updateCond(list, setList, idx, 'parameterId', e.target.value)}
              className="bg-white border common-size15 border-gray-300 rounded px-2 py-2 text-xs text-gray-600 focus:outline-none focus:border-[#358ECD]">
              <option value="">Please Choose A Parameter</option>
              {paramsForLogger(c.loggerId).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select value={c.operator} onChange={e => updateCond(list, setList, idx, 'operator', e.target.value)}
              className="bg-white border common-size15 border-gray-300 rounded px-2 py-2 text-xs text-gray-600 focus:outline-none focus:border-[#358ECD]">
              {OPERATORS.map(o => <option key={o}>{o}</option>)}
            </select>
            <input type="number" placeholder="Number" value={c.value}
              onChange={e => updateCond(list, setList, idx, 'value', e.target.value)}
              className="bg-white border common-size15 border-gray-300 rounded px-2 py-2 text-xs focus:outline-none focus:border-[#358ECD]" />
          </div>
          {list.length > 1 && (
            <button type="button" title="Remove condition" onClick={() => removeCond(list, setList, idx)}
              className="common-size15 mt-1 p-1 text-gray-400 hover:text-red-500 hover:bg-gray-100 rounded transition shrink-0">
              <X className="w-4 h-4" />
            </button>
          )}
          </div>
        </div>
      ))}
      <button type="button" onClick={() => { if (setList === setResetConditions) resetDirtyRef.current = true; setList([...list, { ...emptyCondition(), operator: defaultOp }]) }}
        className="common-size15 font-semibold text-xs text-[#358ECD] hover:underline">+ Add Condition</button>
    </>
  )

  // Portal to <body> so the `fixed inset-0` overlay escapes the react-grid-layout
  // cell's transform-induced stacking context. Without this the modal's z-index is
  // trapped inside its widget cell and later-painted widgets (e.g. the NexSens
  // banner at the bottom of the Overview) bleed through on top of it. This matches
  // every other widget modal (see ModalPortal in DashboardWidgets.jsx).
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">Event Monitor Settings</h2>
          
        </div>

        {error && <div className="mx-6 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded">{error}</div>}

        {/* Body */}
        <div className="flex-1 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-8 px-6 py-5">
          {/* ── LEFT: Base Settings ── */}
          <div>
            <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-3">Base Settings</h3>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Event Name"
              className="w-full border border-[#D6D6D6] rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#358ECD]" />
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" rows={4}
              className="w-full border border-[#D6D6D6] rounded-md px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#358ECD]" />
            <select value={timezone} onChange={e => setTimezone(e.target.value)}
              className="bg-white w-full border border-[#D6D6D6] rounded-md px-3 py-2 text-sm mb-4 focus:outline-none focus:border-[#358ECD]">
              {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
            </select>

            <label className="common-size15 font-semibold flex items-center gap-2 text-xs text-gray-600 mb-3 cursor-pointer">
              <input type="checkbox" checked={restrictRange} onChange={e => setRestrictRange(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" />
              Only send event notification in active date/time range.
            </label>

            <p className="common-size15 text-xs font-semibold text-[#4D4D4D] mb-4 mt-4">Active Days</p>
            <div className="grid grid-cols-3 gap-y-2 gap-x-4 mb-6">
              {DAYS.map(d => (
                <label key={d} className="common-size15 flex items-center gap-2 text-xs text-gray-600 cursor-pointer mb-2">
                  <input type="checkbox" checked={!!activeDays[d]} onChange={e => setActiveDays(prev => ({ ...prev, [d]: e.target.checked }))} className="w-4 h-4 accent-[#358ECD]" />
                  {d}
                </label>
              ))}
            </div>

            <div className="common-size15 flex items-center gap-2 mb-2">
              <span className="common-size15 text-xs font-semibold text-[#4D4D4D] w-32">Active From</span>
              <input type="time" value={activeFrom} onChange={e => setActiveFrom(e.target.value)}
                className="border border-[#D6D6D6] rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="common-size15 text-gray-400">Leave Empty For All Day</span>
            </div>
            <div className="common-size15 flex items-center gap-2 mb-4">
              <span className="common-size15 text-xs font-semibold text-[#4D4D4D] w-32">Active To</span>
              <input type="time" value={activeTo} onChange={e => setActiveTo(e.target.value)}
                className="border border-[#D6D6D6] rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="common-size15 text-gray-400">Leave Empty For All Day</span>
            </div>

            <label className="common-size15 flex items-start gap-2 text-xs text-gray-600 mb-3 cursor-pointer">
              <span className="common-size15 text-xs font-semibold text-[#4D4D4D] w-32 shrink-0">Multi-Trigger</span>
              <input type="checkbox" checked={multiTrigger} onChange={e => setMultiTrigger(e.target.checked)} className="w-4 h-4 accent-[#358ECD] mt-0.5" />
              <span className="common-size15">Check to send email as long as the event is triggered. Uncheck to send email once.</span>
            </label>
            <label className="common-size15 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
              <span className="common-size15 text-xs font-semibold text-[#4D4D4D] w-32 shrink-0">Enable</span>
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" />
              <span className="common-size15">Check to enable this event</span>
            </label>
          </div>

          {/* ── RIGHT: Conditions + Actions ── */}
          <div>
            {/* Trigger Conditions */}
            <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-6">Trigger Conditions</h3>
            <p className="common-size15 text-xs text-[#4D4D4D] mb-4">Condition</p>
            {renderConditionRows(triggerConditions, setTriggerConditions, 'Greater Than', triggerCondOp, setTriggerCondOp)}
            <div className="flex items-center gap-2 mt-2 mb-5">
              <span className="common-size15 text-xs text-[#4D4D4D]">For All Readings In The Last</span>
              <input type="number" value={triggerPeriodMin} onChange={e => setTriggerPeriodMin(e.target.value)}
                className="w-20 border border-[#D6D6D6] rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="common-size15 text-xs text-[#4D4D4D]">Min</span>
            </div>

            {/* Trigger Action */}
            <h3 className="common-size18 text-sm font-bold text-[#4D4D4D] mb-4">Trigger Action <span className="text-xs text-gray-400 font-normal">(Optional)</span></h3>
            <div className="flex gap-4 border-b border-gray-200 mb-3">
              {['Email', 'SMS', 'Push Notifications', 'Advanced'].map(t => (
                <button key={t} type="button" onClick={() => { setActionTab(t); setTestMsg(null) }}
                  className={`common-size15 pb-2 text-sm border-b-2 transition ${actionTab === t ? 'border-[#358ECD] text-[#358ECD]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>{t}</button>
              ))}
            </div>

            {testMsg && (
              <div className={`common-size15 mb-3 px-3 py-2 rounded text-xs border ${testMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'}`}>
                {testMsg.text}
              </div>
            )}

            {actionTab === 'Email' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2">
                    <button type="button" onClick={addEmail}
                      className="mb-size common-size18 px-3 font-bold py-1 border-2 border-[#358ECD] text-[#358ECD] text-xs rounded-md hover:bg-[#358ECD]/10">ADD</button>
                    <button type="button" onClick={editEmail} disabled={emailRecipients.length === 0}
                      className="mb-size common-size18 px-3 font-bold py-1 border-2 border-[#358ECD] text-[#358ECD] text-xs rounded-md hover:bg-[#358ECD]/10 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">EDIT</button>
                    <button type="button" onClick={removeEmail} disabled={emailRecipients.length === 0}
                      className="mb-size common-size18 px-3 font-bold py-1 border-2 border-gray-300 text-gray-600 text-xs rounded-md hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">REMOVE</button>
                  </div>
                  <button type="button" onClick={handleTest} disabled={testing}
                    className="mb-size common-size18 px-3 font-bold py-1 border-2 border-[#358ECD] text-[#358ECD] text-xs rounded-md disabled:opacity-50">{testing ? 'Sending…' : 'TEST'}</button>
                </div>
                <div className="common-size15 grid grid-cols-[74px_1fr_1fr_24px] gap-2 text-[11px] font-semibold text-gray-500 px-1 mb-1"><span>Type</span><span>Recipient</span><span>Notification</span><span></span></div>
                {emailRecipients.length === 0 && <p className="common-size15 text-xs text-gray-400 px-1 py-2">No recipients. Click ADD.</p>}
                {emailRecipients.map((r, i) => (
                  <div key={i} onClick={() => setSelectedEmail(i)}
                    className={`ems-add-field common-size15 grid grid-cols-[74px_1fr_1fr_24px] gap-2 mb-1.5 items-center rounded px-1 -mx-1 ${selectedEmail === i ? 'ring-1 ring-[#358ECD] bg-[#358ECD]/5' : ''}`}>
                    {/* Recipient type: a single email address or a saved group. */}
                    <select value={r.kind || 'email'} onChange={e => setEmailRecipients(prev => prev.map((x, j) => j === i ? { ...x, kind: e.target.value } : x))}
                      className="type-slct border border-[#D6D6D6] rounded-md px-1 py-1 text-xs bg-white focus:outline-none focus:border-[#358ECD]">
                      <option value="email">Email</option>
                      <option value="group">Group</option>
                    </select>
                    {(r.kind || 'email') === 'group' ? (
                      <select value={r.groupId || ''} onChange={e => setEmailRecipients(prev => prev.map((x, j) => j === i ? { ...x, groupId: e.target.value } : x))}
                        className="type-slct border border-[#D6D6D6] rounded-md px-2 py-1 text-xs bg-white focus:outline-none focus:border-[#358ECD]">
                        <option value="">{recipientGroups.length ? 'Choose a group…' : 'No groups for this project'}</option>
                        {recipientGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    ) : (
                      <input ref={el => { emailRefs.current[i] = el }} value={r.to} placeholder="email@example.com" onChange={e => setEmailRecipients(prev => prev.map((x, j) => j === i ? { ...x, to: e.target.value } : x))}
                        className="type-inpt border border-[#D6D6D6] rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                    )}
                    <select value={r.notification} onChange={e => setEmailRecipients(prev => prev.map((x, j) => j === i ? { ...x, notification: e.target.value } : x))}
                      className="type-msg border border-[#D6D6D6] rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]">
                      <option>Message</option><option>Message, Parameter Value</option>
                    </select>
                    <button type="button" title="Remove recipient" onClick={(e) => { e.stopPropagation(); setEmailRecipients(prev => prev.filter((_, j) => j !== i)); setSelectedEmail(null) }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded"><X className="w-4 h-4" /></button>
                  </div>
                ))}

                {/* Create a recipient group without leaving the modal. */}
                {!showNewGroup ? (
                  <button type="button" onClick={() => { setShowNewGroup(true); setGroupError('') }}
                    className="mt-2 text-xs font-semibold text-[#358ECD] hover:underline">+ New recipient group</button>
                ) : (
                  <div className="mt-2 border border-gray-200 rounded-md p-3 bg-gray-50 space-y-2">
                    <div className="text-xs font-bold text-[#4D4D4D]">New Recipient Group</div>
                    <input value={newGroupName} onChange={e => { setNewGroupName(e.target.value); setGroupError('') }} placeholder="Group name"
                      className="w-full border border-[#D6D6D6] rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <textarea value={newGroupRecipients} onChange={e => { setNewGroupRecipients(e.target.value); setGroupError('') }} rows={3}
                      placeholder="Recipient emails — one per line (SMS gateway addresses like 5551234567@tmomail.net work too)"
                      className="w-full border border-[#D6D6D6] rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD] resize-none" />
                    {groupError && <p className="text-xs text-red-600">{groupError}</p>}
                    <div className="flex gap-2">
                      <button type="button" onClick={createGroup} disabled={savingGroup}
                        className="px-3 py-1 bg-[#358ECD] text-white text-xs font-semibold rounded-md hover:bg-[#2d7ab8] disabled:opacity-50">
                        {savingGroup ? 'Saving…' : 'Create Group'}
                      </button>
                      <button type="button" onClick={() => { setShowNewGroup(false); setGroupError('') }}
                        className="px-3 py-1 text-xs text-gray-600 hover:underline">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {actionTab === 'SMS' && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex gap-2">
                    <button type="button" onClick={addSms}
                      className="mb-size common-size18 px-3 font-bold py-1 border-2 border-[#358ECD] text-[#358ECD] text-xs rounded-md hover:bg-[#358ECD]/10">ADD</button>
                    <button type="button" onClick={editSms} disabled={smsRecipients.length === 0}
                      className="mb-size common-size18 px-3 font-bold py-1 border-2 border-[#358ECD] text-[#358ECD] text-xs rounded-md hover:bg-[#358ECD]/10 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">EDIT</button>
                    <button type="button" onClick={removeSms} disabled={smsRecipients.length === 0}
                      className="mb-size common-size18 px-3 font-bold py-1 border-2 border-gray-300 text-gray-600 text-xs rounded-md hover:bg-gray-50 disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent">REMOVE</button>
                  </div>
                  <button type="button" onClick={handleTest} disabled={testing}
                    className="mb-size common-size18 px-3 font-bold py-1 border-2 border-[#358ECD] text-[#358ECD] text-xs rounded disabled:opacity-50">{testing ? 'Sending…' : 'TEST'}</button>
                </div>
                <div className="grid grid-cols-[1fr_1fr_1fr_24px] gap-2 common-size15 font-semibold text-gray-500 px-1 mb-1"><span>Recipient Name</span><span>Phone Number</span><span>Notification</span><span></span></div>
                {smsRecipients.length === 0 && <p className="text-xs text-gray-400 px-1 py-2">No recipients. Click ADD.</p>}
                {smsRecipients.map((r, i) => (
                  <div key={i} onClick={() => setSelectedSms(i)}
                    className={`grid grid-cols-[1fr_1fr_1fr_24px] gap-2 mb-1.5 items-center rounded px-1 -mx-1 ${selectedSms === i ? 'ring-1 ring-[#358ECD] bg-[#358ECD]/5' : ''}`}>
                    <input value={r.name} placeholder="Name" onChange={e => setSmsRecipients(prev => prev.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                      className="border border-[#D6D6D6] rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <input ref={el => { smsRefs.current[i] = el }} value={r.phone} placeholder="Phone" onChange={e => setSmsRecipients(prev => prev.map((x, j) => j === i ? { ...x, phone: e.target.value } : x))}
                      className="border border-[#D6D6D6] rounded-md px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
                    <select value={r.notification} onChange={e => setSmsRecipients(prev => prev.map((x, j) => j === i ? { ...x, notification: e.target.value } : x))}
                      className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]">
                      <option>Message</option><option>Message, Parameter Value</option>
                    </select>
                    <button type="button" title="Remove recipient" onClick={(e) => { e.stopPropagation(); setSmsRecipients(prev => prev.filter((_, j) => j !== i)); setSelectedSms(null) }}
                      className="p-1 text-gray-400 hover:text-red-500 rounded"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}

            {actionTab === 'Push Notifications' && (
              <div className="space-y-2">
                <label className="common-size15 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={pushReceiveMessage} onChange={e => setPushReceiveMessage(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Receive Message
                </label>
                <label className="common-size15 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={pushReceiveParam} onChange={e => setPushReceiveParam(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Receive Parameter Value
                </label>
              </div>
            )}

            {actionTab === 'Advanced' && (
              <div className="space-y-3">
                <label className="common-size15 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={advTransmitEnabled} onChange={e => setAdvTransmitEnabled(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Triggered Transmit/Log Interval
                </label>
                {advTransmitEnabled && (
                  <div className="ml-6 space-y-2">
                    <div className="flex items-center gap-2"><span className="common-size15 text-xs text-gray-600 w-16">Log (min)</span>
                      <input type="number" value={advLog} onChange={e => setAdvLog(e.target.value)} className="w-20 border border-[#D6D6D6] rounded-md px-2 py-1 text-xs" /></div>
                    <div className="flex items-center gap-2"><span className="common-size15 text-xs text-gray-600 w-16">Transmit (min)</span>
                      <input type="number" value={advTransmit} onChange={e => setAdvTransmit(e.target.value)} className="w-20 border bborder-[#D6D6D6] rounded-md px-2 py-1 text-xs" /></div>
                  </div>
                )}
                <label className="common-size15 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={advSendReport} onChange={e => setAdvSendReport(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Send Report
                </label>
                <label className="common-size15 flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
                  <input type="checkbox" checked={advTriggerEWC} onChange={e => setAdvTriggerEWC(e.target.checked)} className="w-4 h-4 accent-[#358ECD]" /> Trigger EWC
                </label>
              </div>
            )}

            {/* Message (shared) */}
            <p className="common-size15 text-xs text-[#4D4D4D] mt-3 mb-1">Message</p>
            <input value={message} onChange={e => setMessage(e.target.value)} placeholder="Event message"
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#358ECD]" />
            <button
              type="button"
              onClick={() => (actionTab === 'SMS' ? addSms() : addEmail())}
              className="common-size15 font-semibold text-xs text-[#358ECD] hover:underline mt-2"
            >+ Add Action</button>

            {/* Reset Conditions */}
            <h3 className="common-size15 text-sm font-semibold text-[#4D4D4D] mt-5 mb-1">Reset Conditions</h3>
            <p className="text-xs text-gray-400 mb-1 italic">Auto-set to the opposite of the trigger. Edit any field to customize.</p>
            <p className="text-xs text-gray-500 mb-2">Condition</p>
            {renderConditionRows(resetConditions, setResetConditions, 'Less Or Equal To', resetCondOp, setResetCondOp)}
            <div className="flex items-center gap-2 mt-4 mb-4">
              <span className="common-size15 text-xs text-gray-600">For All Readings In The Last</span>
              <input type="number" value={resetPeriodMin} onChange={e => setResetPeriodMin(e.target.value)}
                className="w-60 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#358ECD]" />
              <span className="common-size15 text-xs text-gray-600">Min</span>
            </div>

            {/* Reset Action */}
            <div className="flex items-center justify-between mt-4">
              <h3 className="common-size15 text-sm font-semibold text-[#4D4D4D]">Reset Action <span className="text-xs text-gray-400 font-normal">(Optional)</span></h3>
              <button type="button" className="common-size15 text-xs text-[#358ECD] font-semibold hover:underline">+ Add Action</button>
            </div>
            <div className="flex items-center gap-2 mt-6 justify-end">
              <button onClick={onClose} className="common-size15 px-4 py-1.5 border border-gray-200 text-gray-600 text-sm rounded-md hover:bg-gray-50 transition">Cancel</button>
              <button onClick={handleSave} disabled={saving}
                className="common-size15 flex items-center gap-1.5 px-4 py-1.5 bg-[#358ECD] text-white text-sm font-semibold rounded-md hover:bg-[#2d7ab8] transition disabled:opacity-60">
                {saving ? <Loader className="w-4 h-4 animate-spin" /> : null} Save Event
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
